import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, UtensilsCrossed, Leaf, Clock, Instagram } from "lucide-react";
import api from "../lib/api";
import MenuCard from "../components/MenuCard";
import ItemDetailSheet from "../components/ItemDetailSheet";
import { useSettings } from "../context/SettingsContext";

const FADE_UP = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const FADE_IN = { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.7, delay: 0.15 } };

export default function Home() {
  const [menu, setMenu] = useState([]);
  const [active, setActive] = useState(null);
  const [shots, setShots] = useState([]);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/menu").then(({ data }) => setMenu(data)).catch(() => {});
    api.get("/gallery").then(({ data }) => setShots(data.slice(0, 4))).catch(() => {});
  }, []);

  const allItems = menu.flatMap((c) => c.items || []);
  const extras = menu.find((c) => c.slug === "extras")?.items || [];
  const bestSellers = allItems.filter((i) => i.image_url && !i.sold_out).slice(0, 4);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-sapore-purple">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #CDA246 0, transparent 40%), radial-gradient(circle at 80% 70%, #CDA246 0, transparent 35%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-28 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div {...FADE_UP}>
            <img src="/assets/sapore-logo-transparent.png" alt="Sapore Eats" className="h-16 sm:h-20 w-auto mb-6" data-testid="hero-logo" />
            <p className="text-sapore-gold uppercase tracking-[0.3em] text-xs sm:text-sm mb-4">Breakfast &amp; Catering · Est. 2025</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-sapore-cream leading-tight">
              Mornings, done <span className="text-sapore-gold">beautifully.</span>
            </h1>
            <p className="text-sapore-cream/80 mt-5 text-base sm:text-lg max-w-md leading-relaxed">
              Artisanal Nigerian breakfast and cold-pressed juices, made fresh and delivered to your door.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/menu" data-testid="hero-order-cta" className="inline-flex items-center gap-2 bg-sapore-gold text-sapore-ink font-semibold px-7 py-3.5 rounded-full hover:bg-sapore-goldHover transition-colors hover:scale-[1.03] active:scale-[0.97]">
                Order Breakfast <ArrowRight size={18} />
              </Link>
              <Link to="/events" className="inline-flex items-center gap-2 border border-sapore-cream/40 text-sapore-cream font-semibold px-7 py-3.5 rounded-full hover:bg-sapore-cream/10 transition-colors">
                Explore Catering
              </Link>
            </div>
          </motion.div>
          <motion.div {...FADE_IN} className="hidden lg:grid grid-cols-2 gap-4">
            <img src="/assets/breakfast-platter.png" alt="Breakfast platter" className="rounded-2xl object-cover aspect-[3/4] shadow-2xl mt-8" />
            <img src="/assets/french-toast.png" alt="French toast" className="rounded-2xl object-cover aspect-[3/4] shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* Best sellers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-sapore-gold uppercase tracking-[0.25em] text-xs mb-2">Loved by regulars</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-sapore-purple">Best Sellers</h2>
          </div>
          <Link to="/menu" className="hidden sm:inline-flex items-center gap-1 text-sapore-purple font-medium hover:gap-2 transition-all">
            Full menu <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {bestSellers.map((item, i) => (
            <MenuCard key={item.id} item={item} index={i} onOpen={setActive} />
          ))}
        </div>
      </section>

      {/* About */}
      <section className="bg-sapore-creamSurface border-y border-sapore-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20 grid md:grid-cols-3 gap-8">
          {[
            { icon: Leaf, title: "Fresh, always", text: "Juices cold-pressed to order and produce sourced daily." },
            { icon: UtensilsCrossed, title: "Chef-crafted", text: "A considered breakfast menu, plated like a restaurant." },
            { icon: Clock, title: "Order any time", text: "Place your order at any hour — we confirm delivery time by WhatsApp." },
          ].map((f) => (
            <div key={f.title} className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-sapore-purple text-sapore-gold flex items-center justify-center flex-shrink-0">
                <f.icon size={22} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-sapore-purple">{f.title}</h3>
                <p className="text-sapore-inkSoft text-sm mt-1 leading-relaxed">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Catering teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20">
        <div className="rounded-3xl overflow-hidden bg-sapore-purple grid md:grid-cols-2">
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <p className="text-sapore-gold uppercase tracking-[0.25em] text-xs mb-3">Events &amp; Catering</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-sapore-cream leading-tight">
              Private brunches & corporate breakfasts
            </h2>
            <p className="text-sapore-cream/80 mt-4 leading-relaxed">
              From wedding-morning spreads to office breakfast boxes, we cater it all. Tell us about your event and we'll craft a menu.
            </p>
            <Link to="/events" className="inline-flex items-center gap-2 mt-7 bg-sapore-gold text-sapore-ink font-semibold px-7 py-3.5 rounded-full w-fit hover:bg-sapore-goldHover transition-colors">
              Make an enquiry <ArrowRight size={18} />
            </Link>
          </div>
          {shots.length > 0 ? (
            <div className="grid grid-cols-2 gap-1 min-h-[260px]" data-testid="home-catering-gallery">
              {shots.map((g) => (
                <img key={g.id} src={g.url} alt="Sapore event catering" loading="lazy" className="w-full h-full object-cover min-h-[130px]" />
              ))}
            </div>
          ) : (
            <div className="min-h-[260px] bg-cover bg-center" style={{ backgroundImage: "url(/assets/waffle-box.png)" }} />
          )}
        </div>
      </section>

      {/* Instagram strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-sapore-purple">On the gram</h2>
          <a href={settings?.instagram || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sapore-purple font-medium hover:text-sapore-gold">
            <Instagram size={18} /> @sapore.eats
          </a>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["loaded-croissant", "croissant-blt", "french-toast", "breakfast-platter"].map((img) => (
            <div key={img} className="aspect-square rounded-2xl overflow-hidden">
              <img src={`/assets/${img}.png`} alt="Sapore" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </section>

      <ItemDetailSheet item={active} extras={extras} open={!!active} onClose={() => setActive(null)} />
    </div>
  );
}
